import { absBig, BIG_ONE, BIG_TEN, BIG_ZERO } from "@/lib/tool-kit/math/bigint";

/**
 * The decimal expansion of a fraction, found the way long division finds it: divide,
 * keep the remainder, bring down a zero — and when a remainder comes round again, the
 * digits since its first appearance repeat forever.
 */
export interface DecimalExpansion {
  negative: boolean;
  /** The whole-number part, as digits. */
  integer: string;
  /** Digits after the point before any repetition begins ("" if none). */
  nonRepeating: string;
  /** The repeating block ("" when the decimal terminates). */
  repeating: string;
  /** False when the cycle was longer than the cap: the digits are a prefix only. */
  complete: boolean;
}

/** Digits examined before giving up on finding a cycle. */
export const EXPANSION_CAP = 2000;

/** The expansion of num/den, den ≠ 0. The sign is carried separately. */
export function decimalExpansion(
  num: bigint,
  den: bigint,
  cap: number = EXPANSION_CAP
): DecimalExpansion {
  const negative = num < BIG_ZERO !== den < BIG_ZERO && num !== BIG_ZERO;
  const top = absBig(num);
  const bottom = absBig(den);
  const integer = (top / bottom).toString();
  let remainder = top % bottom;
  const digits: string[] = [];
  const seen = new Map<bigint, number>();
  while (remainder !== BIG_ZERO && digits.length < cap) {
    const earlier = seen.get(remainder);
    if (earlier !== undefined) {
      return {
        negative,
        integer,
        nonRepeating: digits.slice(0, earlier).join(""),
        repeating: digits.slice(earlier).join(""),
        complete: true,
      };
    }
    seen.set(remainder, digits.length);
    const scaled = remainder * BIG_TEN;
    digits.push((scaled / bottom).toString());
    remainder = scaled % bottom;
  }
  return {
    negative,
    integer,
    nonRepeating: digits.join(""),
    repeating: "",
    complete: remainder === BIG_ZERO,
  };
}

/** 0.1(6) style text: the repeating block in brackets. Machine-readable, plain text. */
export function expansionToText(e: DecimalExpansion, mark = "."): string {
  const sign = e.negative ? "−" : "";
  if (e.nonRepeating === "" && e.repeating === "") {
    return `${sign}${e.integer}`;
  }
  const repeat = e.repeating === "" ? "" : `(${e.repeating})`;
  const more = e.complete ? "" : "…";
  return `${sign}${e.integer}${mark}${e.nonRepeating}${repeat}${more}`;
}

/**
 * The expansion rounded to `places` decimal places, half up (away from zero), exactly.
 * Returned as digits with the mark, trailing zeros kept: 2/3 to 3 places is 0.667.
 */
export function roundedQuotient(
  num: bigint,
  den: bigint,
  places: number,
  mark = "."
): string {
  const negative = num < BIG_ZERO !== den < BIG_ZERO && num !== BIG_ZERO;
  const top = absBig(num) * BIG_TEN ** BigInt(places);
  const bottom = absBig(den);
  let scaled = top / bottom;
  const rest = top % bottom;
  if (rest * BigInt(2) >= bottom) {
    scaled += BIG_ONE;
  }
  const padded = scaled.toString().padStart(places + 1, "0");
  const cut = padded.length - places;
  const body =
    places === 0
      ? padded
      : `${padded.slice(0, cut)}${mark}${padded.slice(cut)}`;
  const isZero = scaled === BIG_ZERO;
  return `${negative && !isZero ? "−" : ""}${body}`;
}
